import React, { createContext, useContext, useState, useEffect } from 'react';
import { createClient } from '@supabase/supabase-js';
import { Challenge } from '../types';
import { challenges as initialChallenges } from '../data/challenges';

const supabase = createClient(
  import.meta.env.VITE_SUPABASE_URL,
  import.meta.env.VITE_SUPABASE_ANON_KEY
);

interface ChallengeContextType {
  challenges: Challenge[];
  addChallenge: (challenge: Omit<Challenge, 'id'>) => Promise<void>;
  updateChallenge: (id: string, challenge: Challenge) => Promise<void>;
  deleteChallenge: (id: string) => Promise<void>;
  loading: boolean;
  error: string | null;
}

const ChallengeContext = createContext<ChallengeContextType | undefined>(undefined);

export const ChallengeProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [challenges, setChallenges] = useState<Challenge[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    fetchChallenges();
  }, []);

  const fetchChallenges = async () => {
    try {
      const { data, error } = await supabase
        .from('challenges')
        .select('*')
        .order('created_at');

      if (error) throw error;

      if (data.length === 0) {
        // Remove id from initial challenges since the database will generate UUIDs
        const challengesWithoutIds = initialChallenges.map(({ id, ...rest }) => rest);
        
        // Insert all challenges at once
        const { data: insertedData, error: insertError } = await supabase
          .from('challenges')
          .insert(challengesWithoutIds)
          .select();

        if (insertError) throw insertError;
        
        setChallenges(insertedData);
      } else {
        setChallenges(data);
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'An error occurred');
    } finally {
      setLoading(false);
    }
  };

  const addChallenge = async (challenge: Omit<Challenge, 'id'>) => {
    try {
      const { data, error } = await supabase
        .from('challenges')
        .insert([challenge])
        .select()
        .single();

      if (error) throw error;

      setChallenges(prev => [...prev, data]);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to add challenge');
      throw err;
    }
  };

  const updateChallenge = async (id: string, challenge: Challenge) => {
    try {
      const { error } = await supabase
        .from('challenges')
        .update(challenge)
        .eq('id', id);

      if (error) throw error;

      setChallenges(prev => prev.map(c => c.id === id ? challenge : c));
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to update challenge');
      throw err;
    }
  };

  const deleteChallenge = async (id: string) => {
    try {
      const { error } = await supabase
        .from('challenges')
        .delete()
        .eq('id', id);

      if (error) throw error;

      setChallenges(prev => prev.filter(c => c.id !== id));
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to delete challenge');
      throw err;
    }
  };

  return (
    <ChallengeContext.Provider value={{ 
      challenges, 
      addChallenge, 
      updateChallenge, 
      deleteChallenge,
      loading,
      error
    }}>
      {children}
    </ChallengeContext.Provider>
  );
};

export const useChallenges = () => {
  const context = useContext(ChallengeContext);
  if (!context) {
    throw new Error('useChallenges must be used within a ChallengeProvider');
  }
  return context;
};